/**
 * LineChart — Lightweight SVG line chart (zero dependencies)
 *
 * Features:
 *   - Smooth cubic bezier path
 *   - Gradient fill under the line
 *   - Dot markers on each data point
 *   - X axis date labels (month/day)
 *   - Y axis value labels
 *   - Tooltip on touch/hover (nearest-point detection)
 *   - Fully responsive via viewBox
 */

import { useState, useCallback, useRef } from 'react';

export interface ChartDataPoint {
  date: string;   // ISO date (YYYY-MM-DD)
  value: number;
}

interface LineChartProps {
  data: ChartDataPoint[];
  label?: string;
  unit?: string;
  color?: string;
  height?: number;
}

// ── Geometry helpers ─────────────────────────────────────────

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Map a data value into SVG coordinate space */
function toSVGX(index: number, total: number, width: number, padL: number, padR: number): number {
  if (total <= 1) return padL + (width - padL - padR) / 2;
  return padL + ((index / (total - 1)) * (width - padL - padR));
}

function toSVGY(value: number, min: number, max: number, height: number, padT: number, padB: number): number {
  if (max === min) return padT + (height - padT - padB) / 2;
  const t = (value - min) / (max - min);
  return height - padB - t * (height - padT - padB);
}

/** Smooth cubic bezier path through all points */
function buildPath(points: { x: number; y: number }[]): string {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const curr = points[i];
    const cpX = lerp(prev.x, curr.x, 0.5);
    d += ` C ${cpX} ${prev.y}, ${cpX} ${curr.y}, ${curr.x} ${curr.y}`;
  }
  return d;
}

// ── Component ────────────────────────────────────────────────

const W = 320;   // SVG viewBox width
const H = 160;   // SVG viewBox height
const PAD = { l: 40, r: 12, t: 16, b: 32 };

export default function LineChart({
  data,
  label,
  unit = 'lbs',
  color = '#7c3aed',
  height = 160,
}: LineChartProps) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; point: ChartDataPoint } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-ink-faint text-sm">
        No data yet
      </div>
    );
  }

  const values = data.map((d) => d.value);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  // Give Y axis a bit of breathing room
  const yPad = (maxV - minV) * 0.15 || 5;
  const yMin = minV - yPad;
  const yMax = maxV + yPad;

  const points = data.map((d, i) => ({
    x: toSVGX(i, data.length, W, PAD.l, PAD.r),
    y: toSVGY(d.value, yMin, yMax, H, PAD.t, PAD.b),
    data: d,
  }));

  const linePath = buildPath(points.map((p) => ({ x: p.x, y: p.y })));
  const fillPath = linePath + ` L ${points[points.length - 1].x} ${H - PAD.b} L ${points[0].x} ${H - PAD.b} Z`;

  // Y axis tick values
  const yTicks = [yMin, lerp(yMin, yMax, 0.5), yMax].map((v) => Math.round(v));

  // X axis: show first, middle, last date labels
  const xLabelIdxs = data.length <= 3
    ? data.map((_, i) => i)
    : [0, Math.floor((data.length - 1) / 2), data.length - 1];

  const formatDate = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Touch / mouse nearest-point detection
  const handlePointerMove = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const scaleX = W / rect.width;
    const svgX = (e.clientX - rect.left) * scaleX;

    let nearest = points[0];
    let minDist = Infinity;
    for (const p of points) {
      const d = Math.abs(p.x - svgX);
      if (d < minDist) { minDist = d; nearest = p; }
    }
    setTooltip({ x: nearest.x, y: nearest.y, point: nearest.data });
  }, [points]);

  const gradientId = `grad-${label?.replace(/\s/g, '') ?? 'chart'}`;

  return (
    <div className="relative">
      {label && <p className="text-xs text-ink-muted mb-2 px-1">{label}</p>}

      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        style={{ width: '100%', height }}
        onPointerMove={handlePointerMove}
        onPointerLeave={() => setTooltip(null)}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.25" />
            <stop offset="100%" stopColor={color} stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {/* Y grid lines */}
        {yTicks.map((v) => {
          const y = toSVGY(v, yMin, yMax, H, PAD.t, PAD.b);
          return (
            <g key={v}>
              <line x1={PAD.l} y1={y} x2={W - PAD.r} y2={y}
                stroke="#2e2e2e" strokeWidth="1" strokeDasharray="3 3" />
              <text x={PAD.l - 4} y={y + 4} textAnchor="end"
                fontSize="9" fill="#4b5563">{v}</text>
            </g>
          );
        })}

        {/* Gradient fill */}
        <path d={fillPath} fill={`url(#${gradientId})`} />

        {/* Line */}
        <path d={linePath} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />

        {/* Data dots */}
        {points.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3"
            fill={color} stroke="#0a0a0a" strokeWidth="1.5" />
        ))}

        {/* X axis labels */}
        {xLabelIdxs.map((i) => (
          <text key={i}
            x={points[i].x}
            y={H - PAD.b + 14}
            textAnchor="middle"
            fontSize="9" fill="#4b5563"
          >
            {formatDate(data[i].date)}
          </text>
        ))}

        {/* Tooltip */}
        {tooltip && (() => {
          const tx = tooltip.x;
          const ty = tooltip.y;
          const boxW = 72;
          const boxH = 30;
          const bx = Math.min(Math.max(tx - boxW / 2, PAD.l), W - PAD.r - boxW);
          const by = ty - boxH - 8 < PAD.t ? ty + 8 : ty - boxH - 8;
          return (
            <g>
              {/* Vertical crosshair */}
              <line x1={tx} y1={PAD.t} x2={tx} y2={H - PAD.b}
                stroke={color} strokeWidth="1" strokeDasharray="3 3" strokeOpacity="0.5" />
              {/* Highlight dot */}
              <circle cx={tx} cy={ty} r="5"
                fill={color} stroke="#0a0a0a" strokeWidth="2" />
              {/* Box */}
              <rect x={bx} y={by} width={boxW} height={boxH}
                rx="6" fill="#1a1a1a" stroke="#2e2e2e" strokeWidth="1" />
              <text x={bx + boxW / 2} y={by + 11}
                textAnchor="middle" fontSize="9" fill="#9ca3af">
                {formatDate(tooltip.point.date)}
              </text>
              <text x={bx + boxW / 2} y={by + 23}
                textAnchor="middle" fontSize="11" fontWeight="700" fill="#f2f2f2">
                {Math.round(tooltip.point.value)} {unit}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}
